import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { fetchEventsRequest } from '../store/eventSlice';
import { Alert, Box, Button, Card, CardContent, Chip, Container, Skeleton, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PlaceIcon from '@mui/icons-material/Place';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import PaymentsIcon from '@mui/icons-material/Payments';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EventTopBar from '../components/eventDetails/EventTopBar';
import ErrorState from '../components/ErrorState';
import { eventListStyles, topBarStyles } from '../components/eventDetails/eventDetailsStyles';
import { formatCurrency, formatEventDateTime, isSessionAvailable, withBaseUrl } from '../components/eventDetails/eventDetailsUtils';

const getEventTickets = (event) => {
  const sessionTickets = (event.sessions || []).flatMap((session) => session.ticket_types || []);
  return sessionTickets.length ? sessionTickets : event.ticket_types || [];
};

const getFromPrice = (event) => {
  const tickets = getEventTickets(event);
  if (!tickets.length) return null;
  return Math.min(...tickets.map((ticket) => Number(ticket.price || 0)));
};

const getAvailabilityLabel = (event) => {
  if (event.ticket_sales_status === 'paused') return 'Registration paused';
  if (event.ticket_sales_status === 'closed') return 'Registration closed';
  if (event.availability_status === 'scheduled') {
    return 'Registration start soon';
  }

  const sessions = event.sessions || [];
  if (!sessions.length) return event.admin?.ticket_status || 'Registration open';
  return sessions.some((session) => isSessionAvailable(session, event.ticket_types || [])) ? 'Slots available' : 'Sold out';
};

const EventCard = ({ event }) => {
  const imageSrc = withBaseUrl(event.card_image_url || event.hero_image || event.image_url);
  const fromPrice = getFromPrice(event);
  const dateDisplay = formatEventDateTime(event.sale_start || event.event_start || event.date_label);
  const sessionCount = event.sessions?.length || 0;

  return (
    <Card component={RouterLink} to={`/events/${encodeURIComponent(event.slug || event.id)}`} sx={eventListStyles.eventCard}>
      {imageSrc ? (
        <Box component="img" src={imageSrc} alt={event.title} sx={eventListStyles.cardImage} />
      ) : (
        <Box sx={eventListStyles.cardImageFallback}>
          <Typography sx={{ fontWeight: 800 }}>Sidra Medicine Event</Typography>
        </Box>
      )}
      <CardContent sx={eventListStyles.cardContent}>
        <Stack direction="row" spacing={1} useFlexGap alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <Chip size="small" label={event.category || 'Event'} sx={eventListStyles.categoryChip} />
          <Chip size="small" label={getAvailabilityLabel(event)} />
        </Stack>

        <Box>
          <Typography variant="h6" sx={eventListStyles.title}>{event.title}</Typography>
          <Typography variant="body2" sx={eventListStyles.summary}>{event.summary || event.hero_subtitle || ''}</Typography>
        </Box>

        <Stack spacing={1} sx={eventListStyles.metaRow}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CalendarMonthIcon sx={eventListStyles.metaIcon} />
            <Typography variant="body2">{dateDisplay}</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <PlaceIcon sx={eventListStyles.metaIcon} />
            <Typography variant="body2">{event.venue?.name || event.location_label || '-'}</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <PaymentsIcon sx={eventListStyles.metaIcon} />
            <Typography variant="body2">{fromPrice === null ? 'Price TBA' : `From ${formatCurrency(fromPrice)}`}</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <ConfirmationNumberIcon sx={eventListStyles.metaIcon} />
            <Typography variant="body2">{sessionCount ? `${sessionCount} session${sessionCount === 1 ? '' : 's'}` : 'Session details available'}</Typography>
          </Stack>
        </Stack>

        <Button component="span" variant="contained" endIcon={<ArrowForwardIcon />} sx={eventListStyles.detailsButton}>View Details</Button>
      </CardContent>
    </Card>
  );
};

const EventListSkeleton = () => (
  <Grid container spacing={3}>
    {[1, 2, 3].map((item) => (
      <Grid size={{ xs: 12, md: 6, lg: 4 }} key={item}>
        <Skeleton variant="rounded" height={360} />
      </Grid>
    ))}
  </Grid>
);

const EventList = () => {
  const dispatch = useDispatch();
  const { events, loading, error } = useSelector((state) => state.events);

  useEffect(() => {
    document.title = 'Sidra Medicine';
  }, []);

  useEffect(() => {
    dispatch(fetchEventsRequest());
  }, [dispatch]);

  return (
    <Box sx={eventListStyles.page}>
      <Box sx={topBarStyles.shell}>
        <EventTopBar />
      </Box>

      <Box sx={eventListStyles.hero}>
        <Container maxWidth="lg">
          <Chip label="Sidra Medicine" sx={{ bgcolor: '#fff', color: '#1f2937', mb: 2, fontWeight: 700 }} />
          <Typography variant="h3" sx={eventListStyles.heroTitle}>Events & Clinical Education</Typography>
          <Typography variant="h6" sx={eventListStyles.heroSubtitle}>Discover workshops, training programs, and learning experiences for healthcare professionals.</Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={eventListStyles.content}>
        <Box sx={eventListStyles.toolbar}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>Upcoming Listings</Typography>
            <Typography color="text.secondary">Browse available sessions and registration options.</Typography>
          </Box>
          <Chip label={`${events.length} event${events.length === 1 ? '' : 's'}`} />
        </Box>

        {error ? (
          <ErrorState
            error={error}
            title="Unable to load events"
            onRetry={() => dispatch(fetchEventsRequest())}
          />
        ) : loading ? (
          <EventListSkeleton />
        ) : events.length === 0 ? (
          <Alert severity="info">No events are currently available.</Alert>
        ) : (
          <Grid container spacing={3}>
            {events.map((event) => (
              <Grid size={{ xs: 12, md: 6, lg: 4 }} key={event.id}>
                <EventCard event={event} />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default EventList;
